import type { TradeProposal } from "@/components/TradeProposalsCard";

/**
 * Orient a completed trade to the READER.
 *
 * A proposal is ONE shared row, and the list endpoint returns it to BOTH parties
 * (its WHERE matches either direction), so the same row has to read correctly
 * for the proposer and the recipient. Whichever side you are, "gave" must be
 * what left YOUR bag — a history written from the proposer's point of view is
 * backwards for half its audience.
 *
 * Keyed on the FRIEND's id, not the reader's account id. Both would work, but
 * the friend's id comes from the route and is therefore always known, whereas
 * the session account can still be loading on first paint. The earlier version
 * used `account?.id ?? -1`, which silently made `iProposed` false for every row
 * during that window — so every trade the reader had proposed rendered
 * backwards, and then flipped once the session resolved. Wrong data that
 * corrects itself is worse than a spinner: nobody re-reads a row they already
 * believed.
 *
 * Pure, so the flip is unit-testable from both sides.
 */
export function orientCompletedTrade(
  p: Pick<TradeProposal, "fromAccountId" | "giveSpeciesId" | "giveVariant" | "getSpeciesId" | "getVariant">,
  friendId: number,
): { gaveId: number; gaveVariant: string; gotId: number; gotVariant: string } {
  // The proposal's give/get are written from the PROPOSER's side. If the friend
  // proposed it, their "give" is what the reader received.
  const friendProposed = p.fromAccountId === friendId;
  return friendProposed
    ? { gaveId: p.getSpeciesId, gaveVariant: p.getVariant, gotId: p.giveSpeciesId, gotVariant: p.giveVariant }
    : { gaveId: p.giveSpeciesId, gaveVariant: p.giveVariant, gotId: p.getSpeciesId, gotVariant: p.getVariant };
}
